import { useId, useState, type ChangeEvent } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ApiError,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ContractRegisterView,
  ErrorText,
  HelpTip,
  Icon,
  errorMessage,
  type ContractRegisterEntry,
} from '@dataroom/shared';
import {
  useContractRegister,
  useCorrectContract,
  useSellerRoomAccess,
  useUploadCurrentCopy,
} from '@/api/hooks';
import { contentTypeOf, fileToBase64, oversizeMessage } from '@/lib/file';

/**
 * "Contracts" (ADR-0054): the seller's contracts with the dates each document
 * states, earliest expiration first. The seller can correct a date the AI read
 * wrong (audited) and, on a contract, policy or permit that has EXPIRED or
 * expires within 90 days, upload the current copy — stored as a new version of
 * that document, the same way a readable copy replaces a file that could not be
 * read. The server also requires that the seller may write the document (a
 * view-only document answers 403 with a plain message).
 */
export function ContractsPage() {
  const access = useSellerRoomAccess();
  if (access.status === 'login') return <Navigate to="/login" replace />;
  if (access.status === 'loading') {
    return (
      <p className="mx-auto w-full max-w-container px-4 py-6 md:px-6 text-sm text-muted-foreground">
        Loading…
      </p>
    );
  }
  if (access.status === 'forbidden') return <Navigate to="/" replace />;
  return <ContractsPageInner roomId={access.roomId} />;
}

function ContractsPageInner({ roomId }: { roomId: string }) {
  const register = useContractRegister(roomId);
  const correct = useCorrectContract(roomId);
  if (register.error instanceof ApiError && register.error.status === 401) {
    return <Navigate to="/login" replace />;
  }
  return (
    <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Contracts</CardTitle>
          <p className="text-sm text-muted-foreground">
            Your leases, customer and supplier agreements, insurance policies, licenses, permits,
            loans and employment agreements, earliest expiration first.
          </p>
        </CardHeader>
        <CardContent>
          {register.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : register.error ? (
            <ErrorText error={register.error} />
          ) : register.data ? (
            <ContractRegisterView
              register={register.data}
              introAside={<HelpTip id="seller.contracts.flag" />}
              emptyText="No contracts have been filed yet."
              onCorrect={async (documentId, body) => {
                await correct.mutateAsync({ documentId, body });
              }}
              rowAction={(entry) =>
                currentCopyDue(entry) ? <CurrentCopyButton roomId={roomId} entry={entry} /> : null
              }
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

/** A current copy is offered (and accepted by the server) only once a contract is due for one. */
export function currentCopyDue(entry: Pick<ContractRegisterEntry, 'flags'>): boolean {
  return entry.flags.includes('expired') || entry.flags.includes('expires_within_90_days');
}

/** "Upload a current copy": one file, sent as a new version of the contract. */
export function CurrentCopyButton({
  roomId,
  entry,
}: {
  roomId: string;
  entry: Pick<ContractRegisterEntry, 'documentId' | 'name'>;
}) {
  const inputId = useId();
  const upload = useUploadCurrentCopy(roomId);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  async function onPick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const tooBig = oversizeMessage(file);
    if (tooBig) {
      setMessage({ tone: 'error', text: tooBig });
      return;
    }
    setMessage(null);
    try {
      await upload.mutateAsync({
        documentId: entry.documentId,
        body: {
          filename: file.name,
          contentType: contentTypeOf(file),
          contentBase64: await fileToBase64(file),
        },
      });
      setMessage({ tone: 'ok', text: 'Thanks — we have the current copy and are reading it.' });
    } catch (err) {
      setMessage({ tone: 'error', text: errorMessage(err) ?? 'Upload failed. Please try again.' });
    }
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <input
        id={inputId}
        type="file"
        className="sr-only"
        onChange={(e) => void onPick(e)}
        disabled={upload.isPending}
        aria-label={`Upload a current copy of ${entry.name || 'this document'}`}
      />
      <label
        htmlFor={inputId}
        className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-input bg-surface px-3 text-[13px] font-medium hover:bg-surface-2 focus-within:ring-2 focus-within:ring-ring"
      >
        <Icon name="upload" size={14} />
        {upload.isPending ? 'Uploading…' : 'Upload a current copy'}
      </label>
      {message ? (
        <p
          role={message.tone === 'error' ? 'alert' : 'status'}
          className={
            message.tone === 'error' ? 'text-xs text-destructive' : 'text-xs text-st-certified-fg'
          }
        >
          {message.text}
        </p>
      ) : null}
    </div>
  );
}
